import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { TrendingUp, X } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  useCreatePosition,
  useUpdatePosition,
  useDeletePosition,
} from '@/hooks'
import type { ActivePosition } from '@/lib/repositories'

interface PositionManagerSheetProps {
  open: boolean
  onClose: () => void
  position?: ActivePosition
}

export function PositionManagerSheet({
  open,
  onClose,
  position,
}: PositionManagerSheetProps) {
  const { t } = useTranslation()
  const createMutation = useCreatePosition()
  const updateMutation = useUpdatePosition()
  const deleteMutation = useDeletePosition()

  const isEditing = !!position
  const isSubmitting =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending

  const [ticker, setTicker] = useState('')
  const [buyInPrice, setBuyInPrice] = useState('')
  const [openedAt, setOpenedAt] = useState('')
  const [notes, setNotes] = useState('')

  // Reset form when open state or position changes
  useEffect(() => {
    if (open) {
      setTicker(position?.ticker || '')
      setBuyInPrice(position?.buyInPrice?.toString() || '')
      setOpenedAt(
        position?.openedAt || new Date().toISOString().slice(0, 10)
      )
      setNotes(position?.notes || '')
    } else {
      setTicker('')
      setBuyInPrice('')
      setOpenedAt('')
      setNotes('')
    }
  }, [open, position?.id, position?.ticker, position?.buyInPrice, position?.openedAt, position?.notes])

  const isValid =
    ticker.trim().length > 0 && parseFloat(buyInPrice) > 0

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return

    try {
      if (isEditing && position) {
        await updateMutation.mutateAsync({
          id: position.id,
          data: {
            ticker: ticker.trim().toUpperCase(),
            buyInPrice: parseFloat(buyInPrice),
            openedAt: openedAt || null,
            notes: notes.trim() || null,
          },
          previousTicker: position.ticker,
          previousOpenedAt: position.openedAt || '',
        })
        toast.success(t('positionUpdated'))
      } else {
        await createMutation.mutateAsync({
          ticker: ticker.trim().toUpperCase(),
          buyInPrice: parseFloat(buyInPrice),
          openedAt: openedAt || undefined,
          notes: notes.trim() || undefined,
        })
        toast.success(t('positionAdded'))
      }
      onClose()
    } catch {
      toast.error(
        isEditing ? t('failedToUpdatePosition') : t('failedToAddPosition')
      )
    }
  }

  const handleDelete = async () => {
    if (!position || isSubmitting) return
    if (!confirm(t('confirmRemovePosition'))) return

    try {
      await deleteMutation.mutateAsync(position.id)
      toast.success(t('positionRemoved'))
      onClose()
    } catch {
      toast.error(t('failedToRemovePosition'))
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        className="max-h-[90dvh] flex flex-col sm:max-w-md sm:mx-auto p-0"
      >
        <SheetHeader className="px-5 pt-5 pb-4 border-b border-white/[0.06]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/20 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <SheetTitle className="text-white text-base font-semibold">
                  {isEditing ? t('editPosition') : t('addPosition')}
                </SheetTitle>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {isEditing
                    ? t('editPositionSubtitle')
                    : t('addPositionSubtitle')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-neutral-900 border border-white/[0.06] flex items-center justify-center text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Ticker */}
          <div className="space-y-1.5">
            <Label className="text-neutral-300 text-xs">{t('ticker')}</Label>
            <Input
              value={ticker}
              onChange={(e) => setTicker(e.target.value.toUpperCase())}
              onBlur={() => setTicker(ticker.trim())}
              placeholder="AAPL"
              className="bg-neutral-900/80 border-white/[0.06] text-white placeholder:text-neutral-600 h-11 rounded-xl focus:ring-2 focus:ring-emerald-500/30"
              maxLength={10}
            />
          </div>

          {/* Buy-in Price */}
          <div className="space-y-1.5">
            <Label className="text-neutral-300 text-xs">{t('buyIn')}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 text-sm">
                $
              </span>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={buyInPrice}
                onChange={(e) => setBuyInPrice(e.target.value)}
                placeholder="0.00"
                className="bg-neutral-900/80 border-white/[0.06] text-white placeholder:text-neutral-600 h-11 rounded-xl pl-7 focus:ring-2 focus:ring-emerald-500/30"
              />
            </div>
          </div>

          {/* Bought On */}
          <div className="space-y-1.5">
            <Label className="text-neutral-300 text-xs">{t('boughtOn')}</Label>
            <Input
              type="date"
              value={openedAt}
              onChange={(e) => setOpenedAt(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              className="bg-neutral-900/80 border-white/[0.06] text-white h-11 rounded-xl focus:ring-2 focus:ring-emerald-500/30 [color-scheme:dark]"
            />
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label className="text-neutral-300 text-xs">
              {t('notes')} <span className="text-neutral-600">{t('optional')}</span>
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('positionNotesPlaceholder')}
              className="bg-neutral-900/80 border-white/[0.06] text-white placeholder:text-neutral-600 rounded-xl resize-none min-h-[80px] focus:ring-2 focus:ring-emerald-500/30"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 pb-5 pt-4 border-t border-white/[0.06] space-y-2">
          <Button
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            className="w-full h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-black font-semibold hover:from-emerald-400 hover:to-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/20"
          >
            {isSubmitting
              ? t('saving')
              : isEditing
                ? t('saveChanges')
                : t('addPosition')}
          </Button>

          {isEditing && (
            <Button
              variant="ghost"
              onClick={handleDelete}
              disabled={isSubmitting}
              className="w-full h-10 rounded-xl text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              {t('removePosition')}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
